"use client";

import { Children, cloneElement, isValidElement, useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactElement, type ReactNode } from "react";
import { CheckIcon, CloseIcon, CopyIcon, AlertIcon, InfoIcon } from "./icons.tsx";
import { useId } from "./hooks.ts";

/* ── avatar ────────────────────────────────────────────────────────────── */

const AVATAR_SIZES = ["tiny", "small", "", "medium", "large", "hero"] as const;
export type AvatarSize = (typeof AVATAR_SIZES)[number];

function initials(name: string): string {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function Avatar({
  name,
  src,
  size = "",
  square = false,
  className = "",
}: {
  name: string;
  src?: string | null;
  size?: AvatarSize;
  square?: boolean;
  className?: string;
}) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);
  const classes = ["avatar", size, square ? "square" : "", className].filter(Boolean).join(" ");
  return (
    <span className={classes} aria-hidden={src && !failed ? undefined : true}>
      {src && !failed ? (
        <img src={src} alt="" loading="lazy" decoding="async" onError={() => setFailed(true)} />
      ) : (
        initials(name)
      )}
    </span>
  );
}

/* ── switch and checkbox ───────────────────────────────────────────────── */

export function Switch({
  checked,
  onChange,
  label,
  disabled = false,
  id,
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
  label: string;
  disabled?: boolean;
  id?: string;
}) {
  return (
    <button
      type="button"
      id={id}
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      className={`switch${checked ? " on" : ""}`}
      onClick={() => onChange(!checked)}
    >
      <span />
    </button>
  );
}

export function Checkbox({
  checked,
  indeterminate = false,
  onChange,
  label,
  disabled = false,
}: {
  checked: boolean;
  indeterminate?: boolean;
  onChange: (value: boolean) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={indeterminate ? "mixed" : checked}
      aria-label={label}
      disabled={disabled}
      className={`checkbox${checked && !indeterminate ? " on" : ""}${indeterminate ? " some" : ""}`}
      onClick={(event) => {
        event.stopPropagation();
        onChange(!checked);
      }}
    >
      {indeterminate ? <i /> : <CheckIcon size={11} strokeWidth={3} />}
    </button>
  );
}

/* ── badges ────────────────────────────────────────────────────────────── */

export type PillTone = "good" | "warn" | "bad" | "info" | "idle" | "accent";

export function Pill({
  tone = "idle",
  children,
  dot = false,
  pulse = false,
}: {
  tone?: PillTone;
  children: ReactNode;
  dot?: boolean;
  pulse?: boolean;
}) {
  return (
    <span className={`pill ${tone}${pulse ? " pulse" : ""}`}>
      {dot ? <i /> : null}
      {children}
    </span>
  );
}

export function Tag({
  name,
  colour,
  onRemove,
}: {
  name: string;
  colour?: string;
  onRemove?: () => void;
}) {
  return (
    <span className="tag" style={{ ["--tag-colour" as string]: colour ?? "#93a7c2" }}>
      <i />
      {name}
      {onRemove ? (
        <button type="button" onClick={onRemove} aria-label={`Remove ${name}`}>
          <CloseIcon size={9} stroke={3} />
        </button>
      ) : null}
    </span>
  );
}

/* ── field ─────────────────────────────────────────────────────────────── */

/**
 * A labelled control.
 *
 * The label is wired to the control automatically: the field generates an id
 * and clones it onto its child unless the child already has one. Doing it here
 * rather than at every call site is what stops the association being forgotten,
 * and an unassociated label is one a screen reader never reads and a click
 * never focuses.
 */
export function Field({
  label,
  hint,
  error,
  ok,
  children,
  aside,
  htmlFor,
}: {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  ok?: ReactNode;
  children: ReactNode;
  aside?: ReactNode;
  htmlFor?: string;
}) {
  const generated = useId("field");
  const messageId = `${generated}-msg`;
  const message = error ?? ok ?? hint;

  let controlId = htmlFor;
  const wired = Children.map(children, (child) => {
    if (!isValidElement(child)) return child;
    const props = child.props as Record<string, unknown>;
    // A control that already carries an id, or that labels itself through
    // aria-label — every menu and picker in this system does — is left alone.
    if (props.id) {
      controlId ??= String(props.id);
      return child;
    }
    if (props["aria-label"]) return child;
    if (typeof child.type !== "string") return child;
    controlId ??= generated;
    return cloneElement(child as ReactElement<Record<string, unknown>>, {
      id: controlId,
      "aria-describedby": message ? messageId : undefined,
      "aria-invalid": error ? true : undefined,
    });
  });

  return (
    <div className="field">
      {label ? (
        <label htmlFor={controlId}>
          {label}
          {aside ? <span style={{ marginLeft: "auto" }}>{aside}</span> : null}
        </label>
      ) : null}
      {wired}
      {error ? (
        <span className="field-error" id={messageId} role="alert">
          <AlertIcon size={11} />
          {error}
        </span>
      ) : ok ? (
        <span className="field-ok" id={messageId}>
          <CheckIcon size={11} />
          {ok}
        </span>
      ) : hint ? (
        <span className="field-hint" id={messageId}>
          {hint}
        </span>
      ) : null}
    </div>
  );
}

/* ── copy ──────────────────────────────────────────────────────────────── */

/**
 * Copy, with feedback that is unmistakable and short.
 *
 * `navigator.clipboard` needs a secure context and is absent over plain http
 * on a LAN address, which is exactly where somebody tests this from a phone.
 * The textarea fallback is there for that case and nothing else.
 */
export async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through */
  }
  try {
    const area = document.createElement("textarea");
    area.value = text;
    area.setAttribute("readonly", "");
    area.style.position = "fixed";
    area.style.opacity = "0";
    document.body.appendChild(area);
    area.select();
    const done = document.execCommand("copy");
    document.body.removeChild(area);
    return done;
  } catch {
    return false;
  }
}

export function CopyButton({
  value,
  label,
  className = "",
  children,
}: {
  value: string;
  label?: string;
  className?: string;
  children?: ReactNode;
}) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);

  const copy = useCallback(async () => {
    const done = await copyText(value);
    if (!done) return;
    setCopied(true);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), 1400);
  }, [value]);

  return (
    <button
      type="button"
      className={`copy-link${copied ? " copied" : ""} ${className}`.trim()}
      onClick={copy}
      title={copied ? "Copied" : `Copy ${label ?? value}`}
      aria-label={copied ? "Copied" : `Copy ${label ?? value}`}
    >
      <span className="mark">{copied ? <CheckIcon size={12} /> : <CopyIcon size={12} />}</span>
      <span>{children ?? value}</span>
    </button>
  );
}

/* ── save state ────────────────────────────────────────────────────────── */

export type SaveStatus = "idle" | "dirty" | "saving" | "saved" | "failed";

export function SaveState({ status, error }: { status: SaveStatus; error?: string | null }) {
  if (status === "idle") return <span className="save-state" />;
  if (status === "dirty") {
    return (
      <span className="save-state dirty">
        <i />
        Unsaved changes
      </span>
    );
  }
  if (status === "saving") {
    return (
      <span className="save-state saving">
        <span className="spinner" style={{ width: 11, height: 11 }} />
        Saving
      </span>
    );
  }
  if (status === "saved") {
    return (
      <span className="save-state saved">
        <CheckIcon size={12} className="tick" />
        Saved and live
      </span>
    );
  }
  return (
    <span className="save-state failed" role="alert">
      <AlertIcon size={12} />
      {error || "Save failed"}
    </span>
  );
}

/* ── states ────────────────────────────────────────────────────────────── */

export function EmptyState({
  icon,
  title,
  body,
  actions,
}: {
  icon?: ReactNode;
  title: string;
  body?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="panel-empty">
      {icon ? <span className="empty-mark">{icon}</span> : null}
      <strong>{title}</strong>
      {body ? <p>{body}</p> : null}
      {actions ? <div className="panel-empty-actions">{actions}</div> : null}
    </div>
  );
}

export function LoadingState({ label = "Loading" }: { label?: string }) {
  return (
    <div className="loading-block" role="status" aria-live="polite">
      <span className="spinner" style={{ width: 20, height: 20 }} />
      <span style={{ fontSize: 10 }}>{label}</span>
    </div>
  );
}

export function ErrorState({
  title = "Something went wrong",
  body,
  onRetry,
}: {
  title?: string;
  body?: ReactNode;
  onRetry?: () => void;
}) {
  return (
    <div className="panel-empty" role="alert">
      <span className="empty-mark" style={{ color: "var(--red)" }}>
        <AlertIcon size={18} />
      </span>
      <strong>{title}</strong>
      {body ? <p>{body}</p> : null}
      {onRetry ? (
        <div className="panel-empty-actions">
          <button type="button" className="secondary-button compact" onClick={onRetry}>
            Try again
          </button>
        </div>
      ) : null}
    </div>
  );
}

export function Skeleton({ width, height = 12, radius = 6 }: { width?: number | string; height?: number; radius?: number }) {
  return <span className="skeleton" style={{ display: "block", width: width ?? "100%", height, borderRadius: radius }} />;
}

/* ── segmented control ─────────────────────────────────────────────────── */

export function Segments<T extends string>({
  value,
  options,
  onChange,
  label,
}: {
  value: T;
  options: { value: T; label: string; icon?: ReactNode }[];
  onChange: (value: T) => void;
  label: string;
}) {
  /**
   * The selected background is one element that moves, not a colour that
   * blinks off one button and on to the next. Measured rather than assumed,
   * because the options are different widths.
   */
  const group = useRef<HTMLDivElement | null>(null);
  const [thumb, setThumb] = useState<{ left: number; width: number } | null>(null);

  const measure = useCallback(() => {
    const active = group.current?.querySelector<HTMLElement>("button.active");
    setThumb(active ? { left: active.offsetLeft, width: active.offsetWidth } : null);
  }, []);

  useLayoutEffect(measure, [measure, value, options.length]);

  useEffect(() => {
    const observer = new ResizeObserver(measure);
    if (group.current) observer.observe(group.current);
    return () => observer.disconnect();
  }, [measure]);

  return (
    <div className="segments" role="group" aria-label={label} ref={group}>
      <span className="segments-thumb" aria-hidden="true" style={thumb ?? { opacity: 0 }} />
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          className={value === option.value ? "active" : ""}
          aria-pressed={value === option.value}
          onClick={() => onChange(option.value)}
        >
          {option.icon}
          {option.label}
        </button>
      ))}
    </div>
  );
}

/* ── tabs ──────────────────────────────────────────────────────────────── */

/**
 * Arrow keys move between tabs, which is what a tablist is expected to do and
 * what a row of buttons does not do on its own.
 */
export function Tabs<T extends string>({
  value,
  options,
  onChange,
  label,
}: {
  value: T;
  options: { value: T; label: string; badge?: ReactNode; dot?: boolean }[];
  onChange: (value: T) => void;
  label: string;
}) {
  const id = useId("tabs");
  const ref = useRef<HTMLDivElement>(null);

  const onKeyDown = (event: React.KeyboardEvent) => {
    const index = options.findIndex((option) => option.value === value);
    if (index < 0) return;
    let next = index;
    if (event.key === "ArrowRight") next = (index + 1) % options.length;
    else if (event.key === "ArrowLeft") next = (index - 1 + options.length) % options.length;
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = options.length - 1;
    else return;
    event.preventDefault();
    onChange(options[next].value);
    const buttons = ref.current?.querySelectorAll("button");
    buttons?.[next]?.focus();
  };

  return (
    <div className="tabs" role="tablist" aria-label={label} ref={ref} onKeyDown={onKeyDown}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="tab"
          id={`${id}-${option.value}`}
          aria-selected={value === option.value}
          tabIndex={value === option.value ? 0 : -1}
          className={value === option.value ? "on" : ""}
          onClick={() => onChange(option.value)}
        >
          {option.label}
          {option.dot ? <span className="dot" /> : null}
          {option.badge !== undefined ? <span className="nav-count">{option.badge}</span> : null}
        </button>
      ))}
    </div>
  );
}

/* ── tooltip ───────────────────────────────────────────────────────────── */

/**
 * Hover and focus both open it, and it renders into a fixed layer so a panel
 * with `overflow: hidden` cannot clip it.
 */
export function Tooltip({
  content,
  title,
  children,
}: {
  content: ReactNode;
  title?: string;
  children: ReactNode;
}) {
  const [tip, setTip] = useState<{ x: number; y: number } | null>(null);
  const ref = useRef<HTMLSpanElement>(null);

  const show = () => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    setTip({ x: rect.left + rect.width / 2, y: rect.top });
  };

  return (
    <>
      <span
        ref={ref}
        style={{ display: "inline-flex", minWidth: 0 }}
        onPointerEnter={show}
        onPointerLeave={() => setTip(null)}
        onFocusCapture={show}
        onBlurCapture={() => setTip(null)}
      >
        {children}
      </span>
      {tip ? (
        <span
          className="tip"
          role="tooltip"
          style={{
            left: Math.min(Math.max(8, tip.x), (typeof window === "undefined" ? 1200 : window.innerWidth) - 8),
            top: Math.max(8, tip.y - 8),
            transform: "translate(-50%, -100%)",
          }}
        >
          {title ? <b>{title}</b> : null}
          {content}
        </span>
      ) : null}
    </>
  );
}

export function Hint({ children }: { children: ReactNode }) {
  return (
    <Tooltip content={children}>
      <span style={{ color: "var(--faint)", display: "inline-flex", cursor: "help" }}>
        <InfoIcon size={11} />
      </span>
    </Tooltip>
  );
}
